/**
 * Rótulos em português para as movimentações do funil (CRM único).
 * Puro: sem React, sem serviços — usado no Kanban, no card lateral e na Central.
 */

export type CrmMoveLike = {
  triggerType?: string | null;
  outcome?: string | null;
  actorName?: string | null;
  columnName?: string | null;
  fromColumnName?: string | null;
};

const OUTCOME_LABELS: Record<string, string> = {
  moved: 'Movido',
  shadow: 'Sugestão da IA (modo sombra)',
  blocked_low_confidence: 'Não moveu: evidência fraca',
  blocked_no_condition: 'Não moveu: coluna sem condição',
  blocked_terminal_locked: 'Não moveu: card em coluna final',
  blocked_terminal_evidence: 'Não moveu: coluna final exige confirmação na conversa',
  blocked_terminal_unconfirmed: 'Não moveu: venda/perda sem confirmação explícita',
  blocked_manual_cooldown: 'Não moveu: movido por uma pessoa há pouco',
  blocked_stale_evidence: 'Não moveu: evidência anterior à última mudança',
  stale_card: 'Não moveu: card foi movido por alguém durante a análise',
  // Legados (regra antiga)
  no_card: 'Não moveu: contato sem card',
  skipped_protected: 'Não moveu: coluna protegida',
  invalid_target: 'Não moveu: coluna de destino inválida',
  api_error: 'Erro ao avaliar',
};

export function moveOutcomeLabel(outcome?: string | null): string {
  if (!outcome) return '';
  return OUTCOME_LABELS[outcome] ?? outcome;
}

/** Tom visual do resultado: aplicado, sugestão (sombra), bloqueado ou erro. */
export function moveOutcomeTone(outcome?: string | null): 'moved' | 'shadow' | 'blocked' | 'error' {
  if (outcome === 'moved') return 'moved';
  if (outcome === 'shadow') return 'shadow';
  if (outcome === 'api_error') return 'error';
  return 'blocked';
}

export function moveAuthorLabel(log: Pick<CrmMoveLike, 'triggerType' | 'actorName'>): string {
  const actor = log.actorName?.trim();
  switch (log.triggerType) {
    case 'on_conversation':
      return 'IA';
    case 'on_ai_reply':
      return 'IA (regra antiga)';
    case 'manual':
      return actor || 'Usuário';
    case 'registrar':
      return actor || 'Registrar';
    case 'registrar_undo':
      return actor || 'Desfazer do Registrar';
    default:
      return actor || log.triggerType || '';
  }
}

export function isAiTrigger(triggerType?: string | null): boolean {
  return triggerType === 'on_conversation' || triggerType === 'on_ai_reply';
}

/** "Origem → Destino", ou só o destino quando a origem não foi registrada. */
export function moveRouteLabel(log: Pick<CrmMoveLike, 'fromColumnName' | 'columnName'>): string {
  const to = log.columnName?.trim();
  const from = log.fromColumnName?.trim();
  if (from && to) return `${from} → ${to}`;
  return to || '';
}

/** Linha curta do selo no card: "IA moveu: A → B" ou "Movido por Fulano" (rota fica no tooltip). */
export function moveHeadline(log: CrmMoveLike): string {
  if (isAiTrigger(log.triggerType)) {
    const route = moveRouteLabel(log);
    return route ? `IA moveu: ${route}` : 'IA moveu';
  }
  if (log.triggerType === 'registrar_undo') return `Desfeito por ${moveAuthorLabel(log)}`;
  return `Movido por ${moveAuthorLabel(log)}`;
}

const KIND_LABELS: Record<string, string> = {
  entrada: 'Entrada',
  etapa: 'Etapa',
  ganho: 'Ganho',
  perdido: 'Perdido',
};

export function columnKindLabel(kind?: string | null): string {
  if (!kind) return '';
  return KIND_LABELS[kind] ?? kind;
}

export function isTerminalKind(kind?: string | null): boolean {
  return kind === 'ganho' || kind === 'perdido';
}

const LEGACY_WON = ['fechado', 'ganho', 'fechado_ganho', 'vendido'];
const LEGACY_LOST = ['perdido', 'perdido_frio', 'descartado'];

/**
 * Tipo efetivo da coluna (mesma regra do backend, KanbanFunnel.EffectiveKind): o tipo configurado; sem tipo,
 * nome legado de encerramento ("Fechado", "Perdido"…) vira ganho/perdido e a primeira coluna (onde todo contato
 * entra) vira entrada; qualquer outra coluna sem tipo é etapa comum (null).
 */
export function effectiveColumnKind(
  kind: string | null | undefined,
  name: string | null | undefined,
  isFirstColumn: boolean,
): string | null {
  const configured = (kind ?? '').trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(KIND_LABELS, configured)) return configured;
  const key = (name ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .split('')
    .filter((ch) => ch < '\u0300' || ch > '\u036f')
    .map((ch) => (ch === ' ' || ch === '-' ? '_' : ch))
    .join('');
  if (LEGACY_WON.includes(key)) return 'ganho';
  if (LEGACY_LOST.includes(key)) return 'perdido';
  return isFirstColumn ? 'entrada' : null;
}

/**
 * Confiança da movimentação. A IA grava em palavras ("alta", "media", "baixa"); valores numéricos antigos (0–1 ou 0–100)
 * viram porcentagem inteira. Vazio quando ausente ou desconhecido (antes mostrava "NaN%").
 */
export function formatConfidence(confidence?: number | string | null): string {
  if (confidence == null) return '';
  if (typeof confidence === 'string') {
    const key = confidence
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .split('')
      .filter((ch) => ch < '\u0300' || ch > '\u036f')
      .join('');
    if (key === 'alta' || key === 'high') return 'alta';
    if (key === 'media' || key === 'medium') return 'média';
    if (key === 'baixa' || key === 'low') return 'baixa';
    const parsed = Number(key.replace(',', '.'));
    return key !== '' && Number.isFinite(parsed) ? formatConfidence(parsed) : '';
  }
  if (Number.isNaN(confidence)) return '';
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  return `${Math.round(Math.max(0, Math.min(100, pct)))}%`;
}

export function truncateText(text: string | null | undefined, max: number): string {
  if (!text) return '';
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max).trimEnd()}…` : t;
}

function pad2(n: number) {
  return n.toString().padStart(2, '0');
}

/** dd/mm/aaaa hh:mm no fuso local. */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** "agora", "há 5 min", "há 3 h", "ontem", "há 4 dias" e, depois de 7 dias, a data. */
export function formatRelativeTime(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 60_000) return 'agora';
  const min = Math.floor(diffMs / 60_000);
  if (min < 60) return `há ${min} min`;
  const hours = Math.floor(min / 60);
  if (hours < 24 && isSameDay(d, now)) return `há ${hours} h`;
  const days = calendarDaysBetween(d, now);
  if (days <= 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function calendarDaysBetween(a: Date, b: Date): number {
  const da = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const db = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((db - da) / 86_400_000);
}

/** true quando o ISO cai no mesmo dia (local) que `now`. */
export function isToday(iso?: string | null, now: Date = new Date()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return isSameDay(d, now);
}
